import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom'; // VERSION 6 STANDARD
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';

// --- Icônes SVG ---
const IconClock = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;
const IconCheck = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>;
const IconAlert = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>;
const IconBook = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>;
const IconBrain = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z"/><path d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z"/><path d="M15 13a4.5 4.5 0 0 1-3-4 4.5 4.5 0 0 1-3 4"/></svg>;
const IconX = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m18 6-12 12"/><path d="m6 6 12 12"/></svg>;
const IconEye = () => <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>;
const IconArrowRight = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>;
const IconMail = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>;

// --- Utilitaires ---
const cleanLatex = (expression) => {
  if (!expression) return "";
  return expression.replace(/^&/, '').trim();
};

const MathDisplay = ({ expression, isBlock = false }) => {
  if (!expression) return null;
  
  const cleanExpr = cleanLatex(expression);
  const hasLatex = /[\\{}^_]|\\[a-zA-Z]+/.test(cleanExpr);
  
  if (!hasLatex) {
    return (
      <span className="font-medium text-gray-800 leading-relaxed">
        {cleanExpr}
      </span>
    );
  }
  
  try {
    return isBlock ? <BlockMath math={cleanExpr} /> : <InlineMath math={cleanExpr} />;
  } catch (e) {
    console.warn('Erreur LaTeX:', e, 'Expression:', cleanExpr);
    return (
      <span className="font-medium text-gray-800 leading-relaxed">
        {cleanExpr}
      </span>
    );
  }
};

// --- Fonction d'analyse ---
const analyzeExercise = (exercise) => {
  const corrections = exercise.etape_2_corrections || {};
  const studentSolutions = exercise.etape_1_student_solutions || {};
  const referenceSolutions = exercise.reference_solutions || {};
  
  const questionKeys = Object.keys(referenceSolutions);
  const questions = questionKeys.length;
  
  if (questions === 0) {
    return {
      questions: 0, errorsCount: 0, score: 0,
      status: 'warning', statusText: 'Données incomplètes', statusColor: 'text-gray-600 bg-gray-100 border-gray-200'
    };
  }
  
  const errorsCount = questionKeys.reduce((count, qKey) => {
    const correction = corrections[qKey];
    const studentSolution = studentSolutions[qKey];
    const hasExplicitError = correction && correction.first_error;
    const hasNoStudentWork = !studentSolution || Object.keys(studentSolution).length === 0;
    
    if (hasExplicitError || hasNoStudentWork) return count + 1;
    return count;
  }, 0);
  
  const score = questions > 0 ? Math.round(((questions - errorsCount) / questions) * 100) : 0;
  
  let status = 'success';
  let statusText = 'Réussi';
  
  if (errorsCount > 0) {
    if (errorsCount >= questions / 2) {
      status = 'warning'; statusText = 'À revoir';
    } else {
      status = 'partial'; statusText = 'Partiellement réussi';
    }
  }
  
  return { questions, errorsCount, score, status, statusText };
};

// --- Composant Modal Détaillée ---
const ExerciseDetailModal = ({ exercise, onClose }) => {
  if (!exercise) return null;
  
  const dateObj = new Date(exercise.timestamp);
  const dateStr = dateObj.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  const timeStr = dateObj.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  
  const questions = exercise.exercise_context?.questions || {};
  const studentSolutions = exercise.etape_1_student_solutions || {};
  const corrections = exercise.etape_2_corrections || {};
  const referenceSolutions = exercise.reference_solutions || {};
  
  const allQuestionKeys = Object.keys(referenceSolutions).sort((a, b) => 
    a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
  );
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        <div className="bg-slate-900 text-white p-6 flex justify-between items-start">
          <div className="flex-grow">
            <div className="flex items-center gap-3 text-slate-400 text-xs font-mono mb-2">
              <span className="flex items-center gap-2 bg-slate-800 px-2 py-1 rounded">
                <IconClock /> {dateStr} • {timeStr}
              </span>
              <span className="flex items-center gap-1 bg-slate-800 px-2 py-1 rounded">
                ⏱ {exercise.completion_time}s
              </span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold mb-2">Exercice {exercise.exercise_index + 1} - Détails</h2>
          </div>
          <button onClick={onClose} className="bg-slate-800 hover:bg-slate-700 p-2 rounded-lg transition-colors ml-4">
            <IconX />
          </button>
        </div>
        
        {exercise.exercise_statement?.énoncé && (
          <div className="p-6 bg-slate-50 border-b">
            <span className="text-indigo-600 font-bold text-sm uppercase mb-2 block tracking-widest">Énoncé</span>
            <p className="text-slate-800 leading-relaxed">{exercise.exercise_statement.énoncé}</p>
          </div>
        )}
        
        <div className="overflow-y-auto max-h-[60vh] p-6 space-y-6">
          {allQuestionKeys.map((qKey) => {
            const errorInfo = corrections[qKey]?.first_error;
            const errorLineKey = errorInfo?.line;
            const studentSolution = studentSolutions[qKey];
            const hasExplicitError = !!errorInfo;
            const hasNoStudentWork = !studentSolution || Object.keys(studentSolution).length === 0;
            const hasError = hasExplicitError || hasNoStudentWork;
            
            return (
              <div key={qKey} className="border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <span className="bg-slate-700 text-white text-xs font-bold px-2 py-1 rounded">{qKey}</span>
                    {(questions[qKey] || referenceSolutions[qKey]) && (
                      <div className="text-sm text-gray-600 overflow-x-auto">
                        <MathDisplay expression={questions[qKey] || `Réf: ${referenceSolutions[qKey]}`} />
                      </div>
                    )}
                  </div>
                  {hasError ? (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600 bg-red-100 px-2 py-1 rounded-full border border-red-200">
                      <IconAlert /> À revoir
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-green-600 bg-green-100 px-2 py-1 rounded-full border border-green-200">
                      <IconCheck /> Correct
                    </span>
                  )}
                </div>
                
                <div className="p-4 grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h5 className="text-xs font-bold text-gray-400 uppercase mb-2 tracking-wider">Votre réponse</h5>
                    {Object.entries(studentSolutions[qKey] || {}).map(([lineKey, lineContent]) => (
                      <div key={lineKey} className={`flex items-start p-2 rounded-md mb-1 ${lineKey === errorLineKey ? "bg-red-50 border border-red-200" : "hover:bg-gray-50"}`}>
                        <span className="text-xs font-mono text-gray-400 w-8 pt-1 flex-shrink-0">{lineKey}</span>
                        <div className="flex-grow text-gray-800 font-medium overflow-x-auto">
                          <MathDisplay expression={lineContent} />
                        </div>
                      </div>
                    ))}
                    {(!studentSolution || Object.keys(studentSolution).length === 0) && (
                      <div className="text-sm text-red-600 italic p-3 bg-red-50 rounded border border-red-200">
                        ❌ Aucune solution fournie par l'étudiant
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <h5 className="text-xs font-bold text-gray-400 uppercase mb-2 tracking-wider">Correction & Analyse</h5>
                    {referenceSolutions[qKey] && (
                      <div className="mb-4 p-3 bg-green-50 rounded-md border border-green-100 text-green-900 text-sm">
                        <span className="font-semibold block mb-1 text-green-700">Résultat attendu :</span>
                        <div className="overflow-x-auto"><MathDisplay expression={referenceSolutions[qKey]} /></div>
                      </div>
                    )}
                    
                    {(errorInfo || corrections[qKey]?.correction_stepBystep) && (
                      <div className="bg-indigo-50 rounded-lg p-4 border-l-4 border-indigo-500">
                        <div className="flex items-center gap-2 mb-2 text-indigo-900">
                          <IconBrain />
                          <h4 className="font-bold text-sm uppercase">Analyse de l'IA</h4>
                        </div>
                        {errorInfo && (
                          <div className="mb-3 bg-white p-3 rounded border border-indigo-100 shadow-sm">
                            <p className="text-red-600 font-semibold text-sm mb-1 flex items-center gap-2">
                              <IconAlert /> Erreur détectée ligne {errorInfo.line}
                            </p>
                            <p className="text-gray-700 text-sm leading-relaxed">{errorInfo.explanation}</p>
                          </div>
                        )}
                        {corrections[qKey]?.correction_stepBystep && (
                          <div className="text-sm text-indigo-800">
                            <span className="font-semibold">Correction détaillée : </span>
                            {corrections[qKey].correction_stepBystep}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// --- Composant Carte d'Exercice ---
const ExerciseCard = ({ exercise, onClick }) => {
  const analysis = analyzeExercise(exercise);
  const dateObj = new Date(exercise.timestamp);
  const timeStr = dateObj.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  
  return (
    <div onClick={onClick} className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:-translate-y-1 border border-gray-100 overflow-hidden group">
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-4 text-white">
        <div className="flex justify-between items-start mb-3">
          <div>
            <h3 className="text-xl font-bold">Exercice {exercise.exercise_index + 1}</h3>
            <p className="text-indigo-100 text-sm opacity-90">{timeStr}</p>
          </div>
          <div className="bg-white/20 backdrop-blur-sm px-2 py-1 rounded-lg text-xs font-medium">{exercise.completion_time}s</div>
        </div>
        <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-white/20 backdrop-blur-sm`}>
          {analysis.status === 'success' ? <IconCheck /> : <IconAlert />} {analysis.statusText}
        </div>
      </div>
      
      <div className="p-4 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-indigo-600">{analysis.score}%</div>
            <div className="text-xs text-gray-500 font-medium">Score</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-700">{analysis.questions - analysis.errorsCount}/{analysis.questions}</div>
            <div className="text-xs text-gray-500 font-medium">Réussies</div>
          </div>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div className="bg-gradient-to-r from-green-400 to-green-600 h-2 rounded-full transition-all duration-500" style={{ width: `${analysis.score}%` }}></div>
        </div>
        <div className="pt-2 border-t border-gray-100">
          <div className="flex items-center justify-center gap-2 text-indigo-600 group-hover:text-indigo-700 transition-colors">
            <IconEye /> <span className="text-sm font-medium">Voir les détails</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Composant Principal ExamResults ---
const ExamResults = ({ data, onBack }) => {
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [emailSent, setEmailSent] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(false);
  const navigate = useNavigate(); // Utilisation correcte de v6
  
  if (!data || data.length === 0) {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-center p-4">
        <IconBook className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-600 mb-2">Aucune donnée</h2>
      </div>
    );
  }

  // Calculs globaux
  const totalTime = data.reduce((acc, ex) => acc + (ex.completion_time || 0), 0);
  const totalErrors = data.reduce((acc, ex) => {
    const corrections = ex.etape_2_corrections || {};
    return acc + Object.values(corrections).filter(corr => corr?.first_error).length;
  }, 0);
  const avgScore = Math.round(data.reduce((acc, ex) => acc + analyzeExercise(ex).score, 0) / data.length);
  const noteSur20 = Math.round(((avgScore * 20) / 100) * 10) / 10;
  const studentName = data[0]?.student_name || 'Étudiant';
  
  // Récupérer les informations de l'utilisateur connecté
  const userInfo = JSON.parse(localStorage.getItem('user_info') || '{}');
  const userId = userInfo.id;
  const userFirstName = userInfo.firstName || studentName; 

  const handleContinueWithEmail = async () => {
    console.log('🔄 handleContinueWithEmail called, emailSent:', emailSent);
    
    if (emailSent) {
      console.log('✅ Email déjà envoyé, redirection directe...');
      navigate('/smartex-chap');
      return;
    }

    console.log('🚀 Démarrage envoi email...');
    setEmailSent(true);
    
    if (!userId) {
      console.error('❌ User ID manquant');
      alert('❌ Erreur: Informations utilisateur manquantes. Veuillez vous reconnecter.');
      setEmailSent(false);
      return;
    }
    
    try {
      console.log(`📧 Envoi du rapport pour l'utilisateur ${userFirstName} (ID: ${userId})...`);
      
      const formData = new FormData();
      formData.append('user_id', userId);  // Utiliser user_id au lieu de recipient_email
      formData.append('subject', `📚 Résultats SmartEx - ${userFirstName}`);

      console.log('📤 Envoi requête HTTP...');
      const response = await fetch('http://localhost:8000/send-email-report', {
        method: 'POST',
        body: formData,
      });

      console.log('📥 Réponse HTTP reçue, status:', response.status, 'ok:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Erreur HTTP détaillée:', errorText);
        throw new Error(`Erreur HTTP: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const result = await response.json();
      console.log('📧 JSON parsé, réponse backend complète:', result);

      if (result.status === 'success') {
        console.log(`✅ Status success détecté ! Redirection programmée...`);
        setEmailSuccess(true);
        setTimeout(() => {
          console.log('🔄 Exécution de la redirection...');
          // Forcer un reload complet pour reset tous les états
          window.location.href = '/smartex-chap';
        }, 1500);
      } else {
        console.log('❌ Status différent de success:', result.status);
        throw new Error(result.message || result.detail || 'Statut de retour inattendu');
      }
    } catch (error) {
      console.error('❌ Erreur complète envoi email:', error);
      setEmailSent(false);
      setEmailSuccess(false);
      alert(`❌ Erreur: ${error.message}`);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30">
      <div className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Rapport d'Examen</h1>
              <p className="text-gray-600">Analyse détaillée de {data.length} exercices</p>
            </div>
            <div className="text-center">
              <div className="text-xl font-bold text-indigo-600">{noteSur20}/20</div>
              <div className="text-gray-500 text-sm">Note globale</div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="text-2xl font-bold text-indigo-600 mb-2">{studentName}</div>
            <div className="text-gray-600 font-medium">Étudiant</div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="text-2xl font-bold text-green-600 mb-2">{Math.floor(totalTime / 60)}min {totalTime % 60}s</div>
            <div className="text-gray-600 font-medium">Temps total</div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="text-2xl font-bold text-amber-600 mb-2">{totalErrors}</div>
            <div className="text-gray-600 font-medium">Erreurs</div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="text-2xl font-bold text-purple-600 mb-2">{noteSur20}/20</div>
            <div className="text-gray-600 font-medium">Note finale</div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {data.map((exercise, index) => (
            <ExerciseCard
              key={`${exercise.exercise_id}-${index}`}
              exercise={exercise}
              onClick={() => setSelectedExercise(exercise)}
            />
          ))}
        </div>
      </div>
      
      {selectedExercise && (
        <ExerciseDetailModal exercise={selectedExercise} onClose={() => setSelectedExercise(null)} />
      )}
      
      <div className="fixed bottom-8 right-8 z-50">
        <button
          onClick={handleContinueWithEmail}
          disabled={emailSent}
          className={`group flex items-center gap-3 px-6 py-4 ${
            emailSuccess 
              ? 'bg-gradient-to-r from-green-600 to-green-700' 
              : 'bg-gradient-to-r from-indigo-600 to-purple-600'
          } text-white rounded-2xl shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300 font-semibold ${emailSent ? 'opacity-75 cursor-not-allowed' : ''}`}
        >
          {emailSuccess ? (
            <>
              <IconCheck className="w-5 h-5" />
              <span className="text-lg">Email envoyé !</span>
            </>
          ) : emailSent ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border border-white border-t-transparent"></div>
              <span className="text-lg">Envoi en cours...</span>
            </>
          ) : (
            <>
              <IconMail className="w-5 h-5" />
              <span className="text-lg">Terminer et Continuer</span>
            </>
          )}
          <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center group-hover:bg-white/30 transition-colors">
            <IconArrowRight className="text-white" />
          </div>
        </button>
      </div>
    </div>
  );
};

export default ExamResults;